import { MONTH_NAME_TO_INDEX } from 'pages/ModelOverviewPage/data-revamp-new/Helper';

export type YearMonth = { year: number; month: number };
type ParsedModelPeriod = { kind: 'year'; y: number } | { kind: 'month'; y: number; m: number };

/**
 * Converts 2-digit year to 4-digit year.
 * Use cases:
 * - 24 -> 2024
 * - 69 -> 2069
 * - 70 -> 1970
 * - 1999 -> 1999 (unchanged)
 */
export const expandTwoDigitYear = (yy: number): number => {
  if (yy >= 100) return yy;
  return yy + (yy < 70 ? 2000 : 1900);
};

const GENERATED_DUPLICATE_SUFFIX_PATTERNS = [/_duplicated_\d+$/i, /_\d+$/i, /\s+copy(?:\s+\d+)?$/i];

export const stripGeneratedDuplicateSuffix = (raw: string): string => {
  const text = raw.trim();
  const pattern = GENERATED_DUPLICATE_SUFFIX_PATTERNS.find((item) => item.test(text));
  return pattern ? text.replace(pattern, '').trim() : text;
};

export const hasGeneratedDuplicateSuffix = (raw: string): boolean =>
  stripGeneratedDuplicateSuffix(raw) !== raw.trim();

/**
 * Parses raw date-like cell values and returns {year, month}.
 * Supported use cases include:
 * - Period codes (e.g. "P01-2025", "P01-25", "P001-2025", "P001-25") → month from period #
 * - Compact year-month (e.g. "202501", "20251") → Jan 2025
 * - Excel serial date (e.g. "45292")
 * - ISO-ish values (e.g. "2024-03", "2024/03/01")
 * - Month text values (e.g. "Mar-24", "March 2024")
 */
const parseYearMonthCandidate = (raw: string): YearMonth | null => {
  const text = raw.trim();
  if (!text) return null;

  // Period codes: P01-2025, P01-25, P001-2025, P001-25 (P01 / P001 = January)
  let match = text.match(/^p0*(\d{1,2})[\s./-]+(\d{2}|\d{4})$/i);
  if (match) {
    const period = Number(match[1]);
    if (period >= 1 && period <= 12) {
      let year = Number(match[2]);
      if (match[2].length === 2) year = expandTwoDigitYear(year);
      return { year, month: period - 1 };
    }
    return null;
  }

  // Compact year-month: 202501 (YYYYMM) or 20251 (YYYYM for Jan–Sep)
  match = text.match(/^(\d{4})(\d{1,2})$/);
  if (match && (text.length === 5 || text.length === 6)) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    if (month >= 0 && month <= 11) return { year, month };
    return null;
  }

  if (/^\d+(\.\d+)?$/.test(text)) {
    const n = Number(text);
    if (n > 20_000 && n < 60_000) {
      const epoch = Date.UTC(1899, 11, 30);
      const ms = epoch + Math.round(n) * 86_400_000;
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) {
        return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
      }
    }
  }

  match = text.match(/^(\d{4})[\s/-](\d{1,2})(?:[\s/-]\d{1,2})?$/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    if (month >= 0 && month <= 11) return { year, month };
  }

  match = text.match(/^([.A-Za-z]+)[\s./-]+(\d{2}|\d{4})\s*$/);
  if (match) {
    const monKey = match[1].replace(/\./g, '').toLowerCase();
    const month = MONTH_NAME_TO_INDEX[monKey];
    if (month === undefined) return null;
    let year = Number(match[2]);
    if (match[2].length === 2) year = expandTwoDigitYear(year);
    return { year, month };
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return { year: parsed.getFullYear(), month: parsed.getMonth() };
  }

  return null;
};

export const parseYearMonthFromRaw = (raw: string): YearMonth | null => {
  const parsed = parseYearMonthCandidate(raw);
  if (parsed) return parsed;

  const stripped = stripGeneratedDuplicateSuffix(raw);
  if (stripped === raw.trim()) return null;
  return parseYearMonthCandidate(stripped);
};

const parseModelPeriodLabel = (period: string): ParsedModelPeriod | null => {
  const value = period.trim();
  if (/^\d{4}$/.test(value)) {
    return { kind: 'year', y: Number(value) };
  }

  let match = value.match(/^([a-z]{3})[\s/-]?(\d{2})$/i);
  if (match) {
    const month = MONTH_NAME_TO_INDEX[match[1].toLowerCase()];
    if (month !== undefined) {
      return { kind: 'month', y: expandTwoDigitYear(Number(match[2])), m: month };
    }
  }

  match = value.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    if (month >= 0 && month <= 11) return { kind: 'month', y: year, m: month };
  }

  const fromRaw = parseYearMonthFromRaw(value);
  if (fromRaw) return { kind: 'month', y: fromRaw.year, m: fromRaw.month };

  return null;
};

const modelIsYearGranularity = (modelPeriods: string[]): boolean =>
  modelPeriods.length > 0 &&
  modelPeriods.every(
    (x) => x != null && String(x).trim() !== '' && /^\d{4}$/.test(String(x).trim()),
  );

export const normalizeToModelPeriod = (
  rawValue: unknown,
  modelPeriods: string[],
): string | null => {
  if (rawValue == null || modelPeriods.length === 0) return null;

  const raw = String(rawValue).trim();
  if (!raw) return null;

  const directMap = new Map<string, string>();
  modelPeriods.forEach((period) => {
    if (!period) return;
    directMap.set(period.toLowerCase(), period);
    directMap.set(period.toLowerCase().replace(/\s+/g, ''), period);
    directMap.set(period.toLowerCase().replace(/[\s/-]/g, ''), period);
  });

  const directHit =
    directMap.get(raw.toLowerCase()) ||
    directMap.get(raw.toLowerCase().replace(/\s+/g, '')) ||
    directMap.get(raw.toLowerCase().replace(/[\s/-]/g, ''));
  if (directHit) return directHit;

  const rawYm = parseYearMonthFromRaw(raw);
  if (!rawYm) return null;

  const yearlyGranularity = modelIsYearGranularity(modelPeriods);
  const matched = modelPeriods.map((period) => {
    if (!period) return null;
    const parsed = parseModelPeriodLabel(period);
    if (!parsed) return null;
    if (yearlyGranularity && parsed.kind === 'year' && parsed.y === rawYm.year) {
      return period;
    }
    if (
      !yearlyGranularity &&
      parsed.kind === 'month' &&
      parsed.y === rawYm.year &&
      parsed.m === rawYm.month
    ) {
      return period;
    }
    return null;
  });

  return matched.find((item) => item != null) ?? null;
};
